/**
 * Checkout Layout
 *
 * Layout minimalista para páginas de checkout/assinatura.
 * Não mostra sidebar ou navegação - apenas logo, mensagem e botão de logout.
 *
 * Usado quando o usuário DEVE completar o pagamento antes de acessar o sistema.
 */

import React from 'react';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Toolbar,
  Typography,
  useTheme,
  Container,
  Alert,
  alpha,
} from '@mui/material';
import {
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';

// Mapear razões de bloqueio para mensagens amigáveis
const getBlockReasonMessage = (reason: string | undefined): string | null => {
  switch (reason) {
    case 'trial_expired':
      return 'Seu período de teste expirou. Escolha um plano para continuar usando o sistema.';
    case 'subscription_expired':
      return 'Sua assinatura expirou. Renove para continuar usando o sistema.';
    case 'past_due':
      return 'Há um pagamento pendente em sua conta. Regularize para continuar.';
    case 'canceled':
      return 'Sua assinatura foi cancelada. Escolha um plano para reativar.';
    case 'suspended':
      return 'Sua conta está suspensa. Entre em contato com o suporte ou regularize a situação.';
    case 'incomplete':
      return 'Complete o pagamento para ativar sua conta.';
    default:
      return null;
  }
};

const CheckoutLayout: React.FC = () => {
  const { logout, user } = useAuth();
  const { organization } = useOrganization();
  const theme = useTheme();
  const location = useLocation();

  // Extrair razão do bloqueio do state (se houver)
  const locationState = location.state as { reason?: string; organizationName?: string } | null;
  const blockReason = locationState?.reason;
  const blockMessage = getBlockReasonMessage(blockReason);

  const handleLogout = () => {
    logout();
    // O logout já redireciona para /login via AuthContext
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      bgcolor: theme.palette.background.default,
    }}>
      <CssBaseline />

      {/* Header simples */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.primary.main,
          height: '64px',
        }}
      >
        <Toolbar
          sx={{
            height: '64px',
            minHeight: '64px !important',
            px: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Logo e Nome */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={organization?.logoUrl || './logo.png'}
              alt={organization?.name || 'Logo'}
              loading="eager"
              sx={{
                width: 40,
                height: 40,
                mr: 2,
                borderRadius: '10px',
                objectFit: 'cover',
                bgcolor: 'white',
                padding: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            />
            <Box>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.2rem',
                  letterSpacing: '-0.02em',
                  color: 'white',
                }}
              >
                {organization?.name || 'Clinafy'}
              </Typography>
              {user?.email && (
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.8),
                    fontSize: '0.75rem',
                  }}
                >
                  {user.email}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Botão de Logout */}
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              borderColor: alpha(theme.palette.common.white, 0.5),
              color: 'white',
              '&:hover': {
                borderColor: 'white',
                bgcolor: alpha(theme.palette.common.white, 0.1),
              },
            }}
          >
            Sair
          </Button>
        </Toolbar>
      </AppBar>

      {/* Conteúdo Principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: '64px', // Altura do header
          minHeight: '100vh',
          bgcolor: theme.palette.background.default,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Mensagem de bloqueio (se houver) */}
          {blockMessage && (
            <Alert
              severity="warning"
              sx={{
                mb: 3,
                '& .MuiAlert-message': {
                  fontSize: '0.95rem',
                },
              }}
            >
              {blockMessage}
            </Alert>
          )}

          {/* Outlet para o conteúdo da página */}
          <Outlet />
        </Container>
      </Box>

      {/* Footer simples */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          textAlign: 'center',
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Clinafy - Sistema de Gestão para Clínicas
        </Typography>
      </Box>
    </Box>
  );
};

export default CheckoutLayout;
